/**
 * AdminTools.jsx — Owner-tier process / hot-reload controls (#249).
 *
 * Modernized with ThemedTable:
 *   - Live process status with StatusBeacon pulsing health indicators
 *   - Sortable & reorderable process telemetry columns
 *   - Memory and uptime formatting
 *   - Polished typed-confirmation cards
 */
import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { qty } from '../lib/format.js';
import toast from '../lib/toastStore.js';
import ThemedTable, { StatusBeacon } from '../components/ThemedTable/index.js';

function fmtPm2Uptime(ms) {
  if (!ms) return '—';
  const sec = Math.floor((Date.now() - ms) / 1000);
  if (sec < 60) return sec + 's';
  if (sec < 3600) return Math.floor(sec / 60) + 'm';
  if (sec < 86400) return Math.floor(sec / 3600) + 'h';
  return Math.floor(sec / 86400) + 'd';
}

/**
 * Typed-confirm cell. Mirrors the void-ticket UX — operator types the
 * exact phrase before the action button enables.
 */
function ConfirmAction(props) {
  const [armed, setArmed]   = createSignal(false);
  const [typed, setTyped]   = createSignal('');
  const [busy,  setBusy]    = createSignal(false);
  const ready = () => typed().trim() === props.phrase;

  function reset() {
    setArmed(false); setTyped(''); setBusy(false);
  }

  async function fire() {
    if (!ready() || busy()) return;
    setBusy(true);
    try {
      await props.onFire();
      toast.success(`${props.label} — OK.`);
      reset();
    } catch (err) {
      toast.error(`${props.label} failed`, { detail: err?.message });
      setBusy(false);
    }
  }

  return (
    <div style={{
      display: 'flex', 'flex-wrap': 'wrap', gap: '0.5rem',
      'align-items': 'center', 'margin-bottom': '0.5rem',
      padding: '8px 12px', background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)', 'border-radius': '6px',
    }}>
      <Show when={!armed()}>
        <button
          type="button"
          class={props.danger ? 'btn btn-danger' : 'btn'}
          onClick={() => { setArmed(true); }}
        >{props.label}</button>
      </Show>
      <Show when={armed()}>
        <span style={{ 'font-size': '11px', color: 'var(--t-dim, #94a3b8)' }}>
          Type <code style={{ color: 'var(--gold, #ffc857)', 'font-weight': '700' }}>{props.phrase}</code> to confirm:
        </span>
        <input
          type="text"
          class="form-input"
          autofocus
          value={typed()}
          onInput={e => setTyped(e.target.value)}
          placeholder={props.phrase}
          style={{ 'min-width': '160px', 'font-family': 'var(--font-mono, monospace)', padding: '3px 8px', 'font-size': '12px' }}
        />
        <button
          type="button"
          class={props.danger ? 'btn btn-danger' : 'btn'}
          disabled={!ready() || busy()}
          onClick={fire}
        >{busy() ? '…' : 'Execute'}</button>
        <button type="button" class="btn" disabled={busy()} onClick={reset}>Cancel</button>
      </Show>
    </div>
  );
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok || data?.ok === false) {
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
  return data || {};
}

export default function AdminTools() {
  const { data, loading, error, refetch } = useWidgetData('admin-tools', () => ({}));

  const pm2Rows = createMemo(() => Array.isArray(data()?.pm2) ? data().pm2 : []);
  const pm2Err  = createMemo(() => (data()?.pm2 && !Array.isArray(data().pm2)) ? data().pm2.error : null);

  async function softReload(url, body, label) {
    try {
      const r = await postJSON(url, body);
      toast.success(`${label} reloaded${r.loaded != null ? ` (${r.loaded})` : ''}.`);
    } catch (err) {
      toast.error(`${label} reload failed`, { detail: err.message });
    }
  }

  const pm2Columns = [
    {
      key: 'name',
      label: 'Process',
      sortable: true,
      type: 'string',
      canHide: false,
      render: (v) => <strong class="v2-tt-mono" style={{ color: 'var(--t-bright, #fff)' }}>{v}</strong>,
    },
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      type: 'number',
      align: 'center',
      render: (v) => <span class="v2-tt-mono v2-tt-dim">#{v}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      type: 'string',
      render: (v) => (
        <StatusBeacon
          status={v === 'online' ? 'online' : v === 'stopping' ? 'warning' : 'offline'}
          label={v}
        />
      ),
    },
    {
      key: 'uptime',
      label: 'Uptime',
      sortable: true,
      type: 'time',
      align: 'right',
      getValue: r => r.uptime || 0,
      render: (v) => <span class="v2-tt-mono">{fmtPm2Uptime(v)}</span>,
    },
    {
      key: 'restarts',
      label: 'Restarts',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: r => r.restarts || 0,
      render: (v) => (
        <span class="v2-tt-mono" style={{ color: v > 5 ? 'var(--bad, #f87171)' : 'inherit' }}>
          {qty(v)}
        </span>
      ),
    },
    {
      key: 'memoryMb',
      label: 'Memory',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: r => r.memoryMb || 0,
      render: (v) => (
        <span class="v2-tt-mono" style={{ color: v > 1000 ? 'var(--gold, #ffc857)' : 'inherit' }}>
          {v != null ? `${v} MB` : '—'}
        </span>
      ),
    },
  ];

  return (
    <div class="v2-widget">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">🛠️ Owner Controls & Process Monitor</span>
        <button
          type="button"
          class="v2-widget-hdr__btn"
          style={{ 'margin-left': 'auto' }}
          title="Refresh"
          onClick={() => refetch()}
        >↻</button>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-widget__scroll" style={{ padding: '0.5rem 0.75rem', display: 'flex', 'flex-direction': 'column', gap: '12px' }}>

            <div style={{
              'border-left': '3px solid var(--gold, #ffc857)',
              padding: '0.5rem 0.75rem',
              background: 'rgba(255,200,87,0.06)',
              'font-size': '11px',
              'border-radius': '0 6px 6px 0',
            }}>
              <strong style={{ color: 'var(--gold, #ffc857)' }}>⚠ Owner Restricted.</strong> These controls directly affect running Node daemons. Restart actions are rate-limited (30s) and require typed confirmation.
            </div>

            {/* Process Status with ThemedTable */}
            <div>
              <div style={{ 'font-weight': '600', 'font-size': '12px', 'margin-bottom': '6px', color: 'var(--t-bright, #fff)' }}>
                Daemon Process Status
              </div>
              <Show when={pm2Err()}>
                <div class="v2-error" style={{ 'margin-bottom': '8px' }}>PM2 unavailable: {pm2Err()}</div>
              </Show>
              <ThemedTable
                columns={pm2Columns}
                rows={pm2Rows()}
                storageKey="admin_tools_pm2"
                defaultSortKey="id"
                defaultSortDir="asc"
                searchable={false}
                compact={true}
                emptyText="No running PM2 processes found."
              />
              <div class="v2-tt-meta-badge" style={{ 'margin-top': '6px', display: 'inline-block' }}>
                Node {data()?.node || '—'} · Uptime {data()?.uptimeSec ?? 0}s · Host RSS {data()?.memoryMb ?? 0} MB
              </div>
            </div>

            {/* Restart Controls */}
            <div>
              <div style={{ 'font-weight': '600', 'font-size': '12px', 'margin-bottom': '6px', color: 'var(--t-bright, #fff)' }}>
                Restart Controls
              </div>
              <ConfirmAction
                label="🔴 Restart Bot (Full Process Exits)"
                danger
                phrase="RESTART BOT"
                onFire={() => postJSON('/api/admin/tools/restart-bot', { confirm: 'RESTART BOT' })}
              />
              <ConfirmAction
                label="🔄 Restart Web (Soft Route Reload)"
                phrase="RESTART WEB"
                onFire={() => postJSON('/api/admin/tools/restart-web', { confirm: 'RESTART WEB' })}
              />
              <div style={{ 'font-size': '11px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '2px' }}>
                Restart Bot terminates the process — PM2 daemon will automatically respawn it. Web reload re-mounts routes in-flight without dropping active connections.
              </div>
            </div>

            {/* Hot Reload */}
            <div>
              <div style={{ 'font-weight': '600', 'font-size': '12px', 'margin-bottom': '6px', color: 'var(--t-bright, #fff)' }}>
                In-Flight Cache & Route Reloads
              </div>
              <div style={{
                display: 'grid',
                'grid-template-columns': 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '6px',
              }}>
                <button type="button" class="btn" onClick={() => softReload('/api/admin/reload/routes',   {}, 'API Routes')}>↻ Routes</button>
                <button type="button" class="btn" onClick={() => softReload('/api/admin/reload/commands', {}, 'Discord Commands')}>↻ Commands</button>
                <button type="button" class="btn" onClick={() => softReload('/api/admin/reload/caches',   {}, 'Item/Guild Caches')}>↻ Caches</button>
                <button type="button" class="btn" onClick={() => softReload('/api/admin/reload/data', { key: 'items' }, 'Items Data')}>↻ Items</button>
                <button type="button" class="btn" onClick={() => softReload('/api/admin/reload/data', { key: 'sales' }, 'Sales Data')}>↻ Sales</button>
                <button type="button" class="btn" onClick={() => softReload('/api/admin/reload/data', { key: 'users' }, 'Users Data')}>↻ Users</button>
              </div>
            </div>

          </div>
        </Show>
      </Show>
    </div>
  );
}
